import {View} from 'react-native'
import {type MacroMessageDescriptor} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {CLASSROOM_LEVEL_GRADES} from '#/lib/curriculum'
import {
  CLASSROOM_INTERESTS,
  type ClassroomVisibility,
  countryFlag,
  countryName,
  HIGH_SCHOOL_LEVEL,
  levelGuaranteesParents,
  type LevelNameType,
  SECONDARY_SCHOOL_LEVEL,
  STANDARD_LEVELS,
} from '#/state/classrooms'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as TextField from '#/components/forms/TextField'
import {Text} from '#/components/Typography'
import {ClassroomCover} from './ClassroomCover'

/**
 * What a classroom is, as a form.
 *
 * Creating one and changing one ask the same questions, so they ask them with
 * the same fields, in the same order, with the same words — a school that has
 * filled this in once should not have to learn a second form to fix a typo. The
 * two dialogs around it differ in what they do with the answers and in nothing
 * else (see `CreateClassroomDialog` and `EditClassroomDialog`).
 */
export interface ClassroomFormValues {
  name: string
  description: string
  /**
   * The cover picture's bytes, or `''` for none.
   *
   * Bytes and not an address: the editor reads the picture it was handed into the
   * device's own storage and puts the result here (see `ClassroomCover`), because a
   * classroom that kept a `blob:` path would draw a cover until the next reload and
   * never again — and a classroom that kept an upload's URL would draw nothing at
   * all, since the PDS never serves a blob no record references.
   */
  cover: string
  /**
   * Whether parents may join. The creator's answer everywhere it is theirs to
   * give; `effectiveParentsCanJoin` is the answer the dialogs pass on, and a
   * level that guarantees parents overrides it.
   */
  parentsCanJoin: boolean
  visibility: ClassroomVisibility
  levelNameType: LevelNameType
  /**
   * One of the shared levels, used when `levelNameType` is STANDARD — or
   * **empty**: a classroom open to every learner, whatever year they are in.
   */
  level: string
  /**
   * The year inside the level, as a grade *code* (`Level_A1`), when the level
   * has years. Empty when it does not, when the level has none, or when the
   * creator has not picked one yet.
   */
  grade: string
  /** The school's own level name, used when `levelNameType` is SYSTEM. */
  systemLevel: string
  /**
   * The school's own name for a shared level (`University`, `Other`), shown in
   * place of it everywhere the room's level is read. Empty means the shared
   * name, which is what every room had before the field.
   */
  levelLabel: string
  /**
   * Whether the room takes the learners of every country. Only a level that is
   * not a country's school years may say yes ({@link levelTakesOwnName}), and
   * absent on the record means *its own country's learners*, which is the rule
   * the door already reads (`learnerMayJoinClassroom`).
   */
  international: boolean
  interests: string[]
}

export const EMPTY_CLASSROOM_FORM: ClassroomFormValues = {
  name: '',
  description: '',
  cover: '',
  parentsCanJoin: true,
  visibility: 'public',
  levelNameType: 'STANDARD',
  level: STANDARD_LEVELS[1],
  grade: '',
  systemLevel: '',
  levelLabel: '',
  international: false,
  interests: [],
}

/**
 * Whether the level is one the school should name itself.
 *
 * `University` and `Other` are the two levels that are not a country's school
 * years: a university is one institution and `Other` is deliberately a blank. A
 * room at either of them is usually one particular thing — a faculty, a course,
 * a centre — so these are the levels that may carry a name of their own
 * (`levelLabel`), and the same two are where the *international* question is
 * asked, because a room that is not a country's school year is exactly the one
 * that might take students from anywhere.
 */
export function levelTakesOwnName(level: string): boolean {
  return level === 'University' || level === 'Other'
}

/** The years the level teaches, if it teaches years at all. */
export function gradesForLevel(
  level: string,
): {label: MacroMessageDescriptor; value: string}[] {
  return CLASSROOM_LEVEL_GRADES[level] ?? []
}

/**
 * Whether the form's level still needs a year before it can be handed in.
 *
 * Only the levels that teach school years do — Kindergarten, Primary School,
 * Middle School, Secondary School and High School — and only when they are named
 * by their shared name: a private school's own SYSTEM level defines its own
 * years and is not walked through a list it did not write. A classroom with no
 * level, and every level with no years (University, Other), needs nothing more
 * than its name.
 */
export function levelNeedsGrade(values: ClassroomFormValues): boolean {
  return gradeOptionsFor(values).length > 0
}

/** The years to offer for the level the form is actually naming. */
export function gradeOptionsFor(
  values: ClassroomFormValues,
): {label: MacroMessageDescriptor; value: string}[] {
  if (effectiveLevelNameType(values) !== 'STANDARD') return []
  return gradesForLevel(values.level)
}

/** The level a draft actually names, whichever way it was named. */
export function effectiveLevel(values: ClassroomFormValues): string {
  return values.levelNameType === 'SYSTEM' ? values.systemLevel : values.level
}

/**
 * The level-name type only exists for a private classroom: SYSTEM is how a
 * private school uses its own name for a level instead of the shared ones, and
 * a public classroom is one anybody can read and compare, so its levels have to
 * be the shared ones.
 */
export function effectiveLevelNameType(
  values: ClassroomFormValues,
): LevelNameType {
  return values.visibility === 'private' ? values.levelNameType : 'STANDARD'
}

/**
 * Whether the parents rule a draft actually carries.
 *
 * Kindergarten, Primary and Middle School classrooms guarantee parents a way
 * in, so the rule is on there whatever the form says; everywhere else it is the
 * creator's own answer.
 */
export function effectiveParentsCanJoin(values: ClassroomFormValues): boolean {
  return levelGuaranteesParents(effectiveLevel(values)) || values.parentsCanJoin
}

export function ClassroomFields({
  values,
  onChange,
  country,
  lockedParentsReason,
  lockedLevelReason,
  coverEditable = true,
}: {
  values: ClassroomFormValues
  onChange: <K extends keyof ClassroomFormValues>(
    key: K,
    value: ClassroomFormValues[K],
  ) => void
  /**
   * The country the room is in — the school's own, or the one on the record
   * being edited. `null` means *never answered*, and the room is not held to a
   * country then (`learnerMayJoinClassroom` refuses nobody on silence).
   */
  country?: string | null
  /**
   * Why the parents rule cannot be touched, when it cannot. The edit dialog
   * passes it once a parent holds a seat; creation never does.
   */
  lockedParentsReason?: string
  /**
   * Why the level — and the year with it — cannot be touched, when they cannot.
   *
   * The edit dialog passes it while the room's level has not stood for a month
   * yet (`levelChangeLockedUntil`): the level is what everybody in the room was
   * told, so it moves slowly, and the field is held rather than refused on save.
   */
  lockedLevelReason?: string
  /**
   * Whether the cover can be changed here. The cover blob lives in the owner's
   * repo, so only the owner may change it; a moderator's edit leaves it alone.
   */
  coverEditable?: boolean
}) {
  const levelLocked = !!lockedLevelReason
  const t = useTheme()
  const {t: l} = useLingui()
  const isPrivate = values.visibility === 'private'
  const levelNameType = effectiveLevelNameType(values)
  const level = effectiveLevel(values)
  const gradeOptions = gradeOptionsFor(values)
  const guaranteesParents = levelGuaranteesParents(level)
  const parentsLocked = guaranteesParents || !!lockedParentsReason
  const parentsCanJoin = guaranteesParents ? true : values.parentsCanJoin

  return (
    <>
      {/*
        The cover, first, and in both dialogs.

        It is the profile's own picture editor (see `ClassroomCover` for why the
        affordances are borrowed rather than reinvented), it is optional — a
        classroom without one gets the gradient band it has always had — and it is
        the *same field in the same place* whether the room is being made or
        corrected, which is the rule this whole form exists to keep.
      */}
      {coverEditable ? (
        <Field label={l`Cover`}>
          <ClassroomCover
            cover={values.cover}
            onChange={cover => onChange('cover', cover)}
          />
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`Optional. A wide photo — the same 3:1 band a profile uses — shown at the top of the classroom.`}
          </Text>
        </Field>
      ) : null}

      <View style={[a.gap_sm]}>
        <TextField.LabelText>{l`Name`}</TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            testID="classroomNameInput"
            label={l`Classroom name`}
            placeholder={l`1st Year A`}
            maxLength={60}
            defaultValue={values.name}
            onChangeText={value => onChange('name', value)}
          />
        </TextField.Root>
      </View>

      <View style={[a.gap_sm]}>
        <TextField.LabelText>{l`Bio`}</TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            testID="classroomBioInput"
            label={l`Bio`}
            placeholder={l`A short description of this classroom`}
            multiline
            numberOfLines={3}
            maxLength={300}
            defaultValue={values.description}
            onChangeText={value => onChange('description', value)}
          />
        </TextField.Root>
      </View>

      <Field label={l`Who can join`}>
        <View style={[a.flex_row, a.gap_sm]}>
          <Choice
            testID="classroomVisibilityPublic"
            selected={values.visibility === 'public'}
            label={l`Public`}
            onPress={() => onChange('visibility', 'public')}
          />
          <Choice
            testID="classroomVisibilityPrivate"
            selected={values.visibility === 'private'}
            label={l`Private`}
            onPress={() => onChange('visibility', 'private')}
          />
        </View>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {isPrivate
            ? l`A private classroom cannot be found. People join with your code, or by asking.`
            : l`Anyone can find this classroom and ask to join.`}
        </Text>
      </Field>

      <Field label={l`Level`}>
        {isPrivate ? (
          <View style={[a.flex_row, a.gap_sm]}>
            <Choice
              testID="classroomLevelNameStandard"
              selected={values.levelNameType === 'STANDARD'}
              label={l`Standard`}
              onPress={() => onChange('levelNameType', 'STANDARD')}
            />
            <Choice
              testID="classroomLevelNameSystem"
              selected={values.levelNameType === 'SYSTEM'}
              label={l`SYSTEM · name our own`}
              onPress={() => onChange('levelNameType', 'SYSTEM')}
            />
          </View>
        ) : null}

        {levelNameType === 'SYSTEM' ? (
          <TextField.Root>
            <TextField.Input
              testID="classroomSystemLevelInput"
              label={l`Your own level name`}
              placeholder={l`System 3`}
              maxLength={40}
              editable={!levelLocked}
              defaultValue={values.systemLevel}
              onChangeText={value => onChange('systemLevel', value)}
            />
          </TextField.Root>
        ) : (
          <>
            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              {STANDARD_LEVELS.map(option => (
                <Choice
                  key={option}
                  testID={`classroomLevel-${option}`}
                  selected={values.level === option}
                  disabled={levelLocked}
                  label={option}
                  onPress={() => {
                    /*
                     * A year belongs to one level's list, so moving level clears
                     * it — and the school's own level name and the international
                     * answer are about the level it was given at, so they go
                     * with it rather than following the room onto a level that
                     * may not take either.
                     */
                    onChange('level', option)
                    onChange('grade', '')
                    onChange('levelLabel', '')
                    onChange('international', false)
                  }}
                />
              ))}
              {/*
                No level at all: the classroom every learner may join.

                It is a choice of its own rather than the absence of one — a
                school that means "open to all" says so — which is why it sits
                in the same row as the levels it is chosen instead of.
              */}
              <Choice
                testID="classroomLevel-none"
                selected={values.level === ''}
                disabled={levelLocked}
                label={l`No level · open to all`}
                onPress={() => {
                  onChange('level', '')
                  onChange('grade', '')
                  onChange('levelLabel', '')
                  onChange('international', false)
                }}
              />
            </View>
            {/*
              The school's own name for a level that has none of its own.

              `University` and `Other` are the two broad levels, and a school
              that picks either usually means something narrower — a faculty, a
              course, a centre. What it types is printed where the room's level
              is read (`classroomLevelName`) and the shared name stays on the
              record, so the rule that decides who may join does not change.
            */}
            {levelTakesOwnName(values.level) ? (
              <TextField.Root>
                <TextField.Input
                  testID="classroomLevelLabelInput"
                  label={l`What this classroom teaches`}
                  placeholder={
                    values.level === 'University'
                      ? l`Faculty of Engineering`
                      : l`Quran memorisation`
                  }
                  maxLength={60}
                  editable={!levelLocked}
                  defaultValue={values.levelLabel}
                  onChangeText={value => onChange('levelLabel', value)}
                />
              </TextField.Root>
            ) : null}
            {values.level === '' ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {l`A classroom with no level is open to every learner, whatever year they are in.`}
              </Text>
            ) : null}
            {/*
              The two names for one set of years.

              `Secondary School` is the shared level a record carries and
              `High School` is what a learner calls the same years; a school
              picking between them has to be told they are not two different
              audiences, or it will pick one and wonder what it lost. The line
              is shown while either name is selected — where the choice is
              being made — and the year list under it is the same list for both.
            */}
            {level === SECONDARY_SCHOOL_LEVEL || level === HIGH_SCHOOL_LEVEL ? (
              <Text
                testID="classroomSecondaryNamesNote"
                style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {l`Secondary School and High School are the same years under two names — pick the one your school uses.`}
              </Text>
            ) : null}
          </>
        )}

        {isPrivate ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`SYSTEM lets a private school use its own name for a level, instead of the shared ones.`}
          </Text>
        ) : null}

        {lockedLevelReason ? (
          <Text
            testID="classroomLevelLocked"
            style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {lockedLevelReason}
          </Text>
        ) : null}
      </Field>

      {/*
        The country, and the one level where a school may open the room to
        every country.

        A classroom takes the learners of its own country — the school's own
        account is where the country comes from, and it is not a choice the form
        offers — with one exception, stated here rather than assumed: a
        University or an `Other` room may be **international**, which is the
        school saying out loud that its audience is not one country's students.
        The wording is deliberate: an international classroom is a *different*
        audience, not a better one, and a school ticking it to look grander has
        been told what it is choosing.
      */}
      <Field label={l`Country`}>
        {country ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`Learners in ${countryFlag(country)} ${countryName(country)} can join this classroom. A learner in another country cannot.`}
          </Text>
        ) : (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`Your account does not name a country yet, so this classroom is not held to one.`}
          </Text>
        )}

        {levelTakesOwnName(level) ? (
          <View style={[a.flex_row, a.gap_sm]}>
            <Choice
              testID="classroomCountryOnly"
              selected={!values.international}
              label={l`This country's learners`}
              onPress={() => onChange('international', false)}
            />
            <Choice
              testID="classroomInternational"
              selected={values.international}
              label={l`International`}
              onPress={() => onChange('international', true)}
            />
          </View>
        ) : null}
        {levelTakesOwnName(level) ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {values.international
              ? l`An international classroom takes learners from every country. It is a different audience, not a better one.`
              : l`An international classroom takes learners from every country — a different audience, not a better one. Most classrooms stay with their own country's learners.`}
          </Text>
        ) : null}
      </Field>

      {/*
        The year, for the levels that have years.

        Kindergarten, Primary, Middle, Secondary and High School are levels a
        school year belongs to, and a classroom at one of them names the year it
        takes: the people who can join it are the learners in that year
        (`learnerMayJoinClassroom`) and the work made in it is pinned to the
        same year when it is published, because the room's year and its work's
        year are the same code. University and Other have no year of their own,
        and a classroom with no level is open to all — none of those asks this
        question, and none of them shows it.

        Secondary School and High School share one list of years
        (`CAPSULE_GRADES.secondary`), which is the visible form of the one fact
        the two names have always carried: they are the same years (see the line
        under the level choices, which says it in words).
      */}
      {gradeOptions.length > 0 ? (
        <Field label={l`Year`}>
          <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
            {gradeOptions.map(option => (
              <Choice
                key={option.value}
                testID={`classroomGrade-${option.value}`}
                selected={values.grade === option.value}
                disabled={levelLocked}
                label={l(option.label)}
                onPress={() => onChange('grade', option.value)}
              />
            ))}
          </View>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`The year this classroom teaches. Learners of this year can join, and the work published in it is set at this level.`}
          </Text>
        </Field>
      ) : null}

      {/*
        Whether parents may join.

        It follows the level, because the level is where the rule comes from:
        the youngest classrooms guarantee parents a seat and the choice is
        simply not the creator's there, so the control says so and does not
        move. It is also the thing a room can decide only while no parent has a
        seat yet — the edit dialog locks it once one does.
      */}
      <Field label={l`Parents`}>
        <View style={[a.flex_row, a.gap_sm]}>
          <Choice
            testID="classroomParentsCanJoin"
            selected={parentsCanJoin}
            disabled={parentsLocked}
            label={l`Parents can join`}
            onPress={() => onChange('parentsCanJoin', true)}
          />
          <Choice
            testID="classroomParentsNoJoin"
            selected={!parentsCanJoin}
            disabled={parentsLocked}
            label={l`No parents`}
            onPress={() => onChange('parentsCanJoin', false)}
          />
        </View>
        {guaranteesParents ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`Parents can always join a Kindergarten, Primary School or Middle School classroom.`}
          </Text>
        ) : lockedParentsReason ? (
          <Text
            testID="classroomParentsLocked"
            style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {lockedParentsReason}
          </Text>
        ) : level === SECONDARY_SCHOOL_LEVEL || level === HIGH_SCHOOL_LEVEL ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`Secondary School and High School classrooms can go either way — parents can join, or stay outside. You can change this later while no parent has joined.`}
          </Text>
        ) : null}
      </Field>

      <Field label={l`Interests`}>
        <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
          {CLASSROOM_INTERESTS.map(interest => (
            <Choice
              key={interest}
              testID={`classroomInterest-${interest}`}
              selected={values.interests.includes(interest)}
              label={interest}
              onPress={() =>
                onChange(
                  'interests',
                  values.interests.includes(interest)
                    ? values.interests.filter(i => i !== interest)
                    : [...values.interests, interest],
                )
              }
            />
          ))}
        </View>
      </Field>
    </>
  )
}

export function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <View style={[a.gap_sm]}>
      <Text style={[a.text_md, a.font_semi_bold]}>{label}</Text>
      {children}
    </View>
  )
}

export function Choice({
  selected,
  label,
  onPress,
  testID,
  disabled,
}: {
  selected: boolean
  label: string
  onPress: () => void
  testID?: string
  disabled?: boolean
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="small"
      color={selected ? 'primary' : 'secondary'}
      disabled={disabled}
      onPress={onPress}>
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}
